import { Can } from '@/domains/access-control';
import { PageHeader } from '@/shared/components/PageHeader';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { MOBILE_SSO_REQUIRED_FIELDS } from '@skewb-works/contracts';
import type { MobileSsoConfig, UpsertMobileSsoConfigBody } from '@skewb-works/contracts';
import { KeyRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMobileSsoConfigs, useUpsertMobileSsoConfig } from '../hooks/useMobileSsoConfig';

type FormState = {
  ssoEnabled: boolean;
  identifierUrl: string;
  scopes: string;
  idpProvider: string;
  cognitoDomain: string;
  cognitoPoolId: string;
  cognitoClientId: string;
  cognitoRegion: string;
  cognitoGroup: string;
};

const TEXT_FIELDS: { key: Exclude<keyof FormState, 'ssoEnabled'>; label: string; hint?: string }[] =
  [
    {
      key: 'cognitoGroup',
      label: 'Mobile-access Cognito group',
      hint: 'Password users are added to this group when provisioned. SSO users are added by EAR (MOBILE_ACCESS_GROUP) on first login.',
    },
    { key: 'idpProvider', label: 'IdP provider', hint: 'e.g. YorkshireWaterMobileAD' },
    { key: 'identifierUrl', label: 'Identifier URL', hint: 'e.g. yorkshirewater.co.uk' },
    {
      key: 'scopes',
      label: 'Scopes',
      hint: "e.g. ['openid', 'email', 'aws.cognito.signin.user.admin']",
    },
    { key: 'cognitoDomain', label: 'Cognito domain' },
    { key: 'cognitoPoolId', label: 'Cognito pool ID' },
    { key: 'cognitoClientId', label: 'Cognito client ID' },
    { key: 'cognitoRegion', label: 'Cognito region' },
  ];

function toForm(config?: MobileSsoConfig): FormState {
  return {
    ssoEnabled: config?.ssoEnabled ?? false,
    identifierUrl: config?.identifierUrl ?? '',
    scopes: config?.scopes ?? '',
    idpProvider: config?.idpProvider ?? '',
    cognitoDomain: config?.cognitoDomain ?? '',
    cognitoPoolId: config?.cognitoPoolId ?? '',
    cognitoClientId: config?.cognitoClientId ?? '',
    cognitoRegion: config?.cognitoRegion ?? '',
    cognitoGroup: config?.cognitoGroup ?? '',
  };
}

function ClientConfigCard({
  earClientId,
  config,
}: { earClientId: number; config?: MobileSsoConfig }) {
  const upsert = useUpsertMobileSsoConfig();
  const [form, setForm] = useState<FormState>(() => toForm(config));

  useEffect(() => setForm(toForm(config)), [config]);

  const missing = form.ssoEnabled
    ? MOBILE_SSO_REQUIRED_FIELDS.filter((field) => !form[field].trim())
    : [];

  const handleSave = () => {
    const body: UpsertMobileSsoConfigBody = { ...form };
    upsert.mutate({ earClientId, body });
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Mobile client {earClientId}</h2>
        {config?.updatedBy && (
          <span className="text-xs text-muted-foreground">Last updated by {config.updatedBy}</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id={`sso-enabled-${earClientId}`}
          checked={form.ssoEnabled}
          onCheckedChange={(checked) => setForm({ ...form, ssoEnabled: checked === true })}
        />
        <Label htmlFor={`sso-enabled-${earClientId}`}>
          SSO enabled (offers “SSO login?” when adding mobile users)
        </Label>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {TEXT_FIELDS.map(({ key, label, hint }) => {
          const invalid = missing.includes(key as (typeof MOBILE_SSO_REQUIRED_FIELDS)[number]);
          return (
            <div key={key} className="space-y-1">
              <Label htmlFor={`${key}-${earClientId}`}>{label}</Label>
              <Input
                id={`${key}-${earClientId}`}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className={invalid ? 'border-red-500' : ''}
              />
              {invalid ? (
                <p className="text-xs text-red-500">Required when SSO is enabled</p>
              ) : (
                hint && <p className="text-xs text-muted-foreground">{hint}</p>
              )}
            </div>
          );
        })}
      </div>

      <Can action="update" subject="AccessControl">
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={upsert.isPending || missing.length > 0}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {upsert.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Can>
    </Card>
  );
}

export default function MobileSsoConfigPage() {
  const { data, isLoading, isError } = useMobileSsoConfigs();

  const configs = data?.data ?? [];
  const defaultId = data?.defaultEarClientId ?? null;
  // Always offer a card for this deployment's client, even before it has a row.
  const needsDefaultCard = defaultId !== null && !configs.some((c) => c.earClientId === defaultId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mobile SSO"
        subtitle="Per-client SSO connection and mobile-access group for provisioning mobile users"
        icon={<KeyRound className="w-8 h-8 text-orange-600" />}
      />

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {isError && <p className="text-sm text-red-500">Failed to load SSO configuration.</p>}
      {data && configs.length === 0 && !needsDefaultCard && (
        <p className="text-sm text-muted-foreground">
          No mobile client is configured for this deployment (MOBILE_DEFAULT_CLIENT_ID is unset).
        </p>
      )}

      {needsDefaultCard && <ClientConfigCard earClientId={defaultId} />}
      {configs.map((config) => (
        <ClientConfigCard
          key={config.earClientId}
          earClientId={config.earClientId}
          config={config}
        />
      ))}
    </div>
  );
}
